import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api, queryString } from '@/lib/api';
import type { StatusTone } from '@/components/ui/status-badge';
import type {
  AdminExpenseView,
  ApproveExpenseInput,
  CreateExpenseInput,
  DeclineExpenseInput,
  ExpenseCategoryValue,
  ExpenseStatusValue,
  ExpenseTotals,
  ExpenseView,
} from '@shared/schemas/expense';
import type { ListResponse, RecordSort } from '@shared/schemas/pagination';

/* The queries behind /admin/expenses and the cleaner's own list. ROADMAP.md §10.1. */

const ADMIN_ROOT = ['admin', 'expenses'] as const;
const MINE_ROOT = ['employee', 'expenses'] as const;

/**
 * The badge tone per state — one mapping, so no screen invents its own.
 *
 * `declined` is `cancelled`-toned rather than alarming, the same call
 * `JOB_REQUEST_TONES` made: a declined expense is a normal answer, not a fault.
 */
export const EXPENSE_TONES: Record<ExpenseStatusValue, StatusTone> = {
  pending: 'pending',
  approved: 'completed',
  declined: 'cancelled',
};

export interface AdminExpenseListParams {
  sort: RecordSort;
  page: number;
  status: ExpenseStatusValue | 'all';
  category: ExpenseCategoryValue | 'all';
  employeeId?: string;
  from?: string;
  to?: string;
}

export interface AdminExpenseList extends ListResponse<AdminExpenseView> {
  totals: ExpenseTotals;
}

function adminInvalidate(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: ADMIN_ROOT }).then(() => undefined);
}

export function useAdminExpenses(params: AdminExpenseListParams) {
  return useQuery({
    queryKey: [...ADMIN_ROOT, 'list', params],
    queryFn: () => api.get<AdminExpenseList>(`/api/admin/expenses${queryString({ ...params })}`),
    // Keeps the previous page on screen while the next one loads, so the table
    // does not collapse to a skeleton on every click of Next.
    placeholderData: (previous) => previous,
  });
}

export function useApproveExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; input: ApproveExpenseInput }) =>
      api.post<AdminExpenseView>(`/api/admin/expenses/${args.id}/approve`, args.input),
    onSuccess: () => adminInvalidate(queryClient),
  });
}

export function useDeclineExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; input: DeclineExpenseInput }) =>
      api.post<AdminExpenseView>(`/api/admin/expenses/${args.id}/decline`, args.input),
    onSuccess: () => adminInvalidate(queryClient),
  });
}

export function useReopenExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<AdminExpenseView>(`/api/admin/expenses/${id}/reopen`),
    onSuccess: () => adminInvalidate(queryClient),
  });
}

export function useCreateAdminExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateExpenseInput) =>
      api.post<AdminExpenseView>('/api/admin/expenses', input),
    onSuccess: () => adminInvalidate(queryClient),
  });
}

/**
 * Attach a receipt.
 *
 * Multipart through `api.upload`, which keeps the P22 contract: a refused file
 * throws an `ApiError` carrying the server's own words ("A receipt has to be a
 * PDF or a photo"), so a caller cannot treat a rejection as a success.
 */
export function useUploadAdminReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; form: FormData }) =>
      api.upload<AdminExpenseView>(`/api/admin/expenses/${args.id}/receipt`, args.form),
    onSuccess: () => adminInvalidate(queryClient),
  });
}

// ---------------------------------------------------------------------------
// The cleaner's own
// ---------------------------------------------------------------------------

export interface MyExpenseListParams {
  page: number;
  status: ExpenseStatusValue | 'all';
}

export function useMyExpenses(params: MyExpenseListParams) {
  return useQuery({
    queryKey: [...MINE_ROOT, 'list', params],
    queryFn: () =>
      api.get<ListResponse<ExpenseView>>(`/api/employee/expenses${queryString({ ...params })}`),
    placeholderData: (previous) => previous,
  });
}

function mineInvalidate(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: MINE_ROOT }).then(() => undefined);
}

export function useSubmitMyExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateExpenseInput) =>
      api.post<ExpenseView>('/api/employee/expenses', input),
    onSuccess: () => mineInvalidate(queryClient),
  });
}

export function useUploadMyReceipt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; form: FormData }) =>
      api.upload<ExpenseView>(`/api/employee/expenses/${args.id}/receipt`, args.form),
    onSuccess: () => mineInvalidate(queryClient),
  });
}
